import type { Event } from '@cocoonspace/sdk-js/types/event'
import { dateTimeUtils } from '@cocoonspace/shared/utils/date-time-utils'
import { Users } from 'lucide-react'
import { useTranslation } from 'next-i18next'
import type {
	ComponentProps,
	MouseEventHandler,
	PropsWithChildren,
} from 'react'
import { Skeleton } from '~/components/ui/skeleton'
import { EventDateBadge } from '~/domains/events/components/atoms/event-date-badge'
import { cn } from '~/lib/utils'

export const EventDateTimeInfos = ({
	event,
	children,
	altStyle,
	onEventNameClick,
	showSpaceTitle,
	...props
}: PropsWithChildren<
	{
		event: Event
		altStyle?: boolean
		showSpaceTitle?: boolean
		onEventNameClick?: MouseEventHandler<HTMLDivElement>
	} & ComponentProps<'div'>
>) => {
	const { t } = useTranslation()

	const eventTitle = event.title || (t('cwEvents:eventCocoonSpace') as string)

	return (
		<div
			{...props}
			className={cn('flex items-center gap-4 overflow-x-auto', props.className)}
		>
			<EventDateBadge
				date={event.start_local!}
				timezone={event.space?.timezone}
				altStyle={altStyle}
			/>

			<div className='flex w-full flex-1 flex-col overflow-x-auto'>
				<div className='flex w-full flex-row justify-between gap-x-4 font-bold leading-none'>
					<div
						className={cn('mb-1 truncate text-lg', {
							'cursor-pointer': !!onEventNameClick,
						})}
						title={eventTitle}
						onClick={onEventNameClick}
					>
						{eventTitle}
					</div>

					<div>{children}</div>
				</div>

				{event.type === 'desk' && (
					<div className='flex flex-row items-center gap-1 text-sm'>
						<Users size={14} />

						{t('space:sidebarDesk.person', { count: event.qty })}
					</div>
				)}

				{event.type !== 'desk' && (
					<div>
						{t('common:fromTo', {
							start: dateTimeUtils(`${event.start_local}`)
								.tz(event.space?.timezone)
								.customFormat('hours'),
							end: dateTimeUtils(`${event.end_local}`)
								.tz(event.space?.timezone)
								.customFormat('hours'),
						})}
					</div>
				)}

				{showSpaceTitle && <div className='text-sm'>{event.space?.title}</div>}
			</div>
		</div>
	)
}

EventDateTimeInfos.skeleton = ({
	className,
	...props
}: ComponentProps<'div'>) => (
	<div
		{...props}
		className={cn('flex flex-row items-center gap-4', className)}
	>
		<EventDateBadge.skeleton />

		<div className='flex-1 overflow-x-hidden'>
			<Skeleton className='mb-1 h-[16px] w-[175px]' />

			<Skeleton className='h-[16px] w-[120px]' />
		</div>
	</div>
)
